import isEqual from 'fast-deep-equal';
import { INIT_NEW_HABIT } from 'assets/data';
import { ACTIVITY_TYPES, DEFAULT_DURATION } from 'constants/general';
import { ActivityType, ChoiceType } from 'interfaces/common.interface';
import {
  SettingsType,
  TimerModeToggleInput,
  TimerModeToggleResult
} from 'interfaces/setting.interface';
import { areDaysOfWeekSetsEqual } from 'utils/daysOfWeek.util';
import { normalizeHabitName } from 'utils/helpers/normalizeHabitName';
import { isNumber } from 'utils/number.util';
import { t } from 'services/i18n.service';
import { MAIN_ROW_FIELD_KEYS } from 'constants/settings';

const pickMoreOptionsFields = (
  activity: ActivityType
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  (Object.keys(activity) as (keyof ActivityType)[]).forEach((key) => {
    if (!MAIN_ROW_FIELD_KEYS.has(key)) {
      result[key as string] = activity[key];
    }
  });

  return result;
};

export const getDefaultNewHabitBaseline = (
  activityType: string
): ActivityType => {
  const base = INIT_NEW_HABIT();
  let durationSeconds = base.duration_seconds;

  switch (activityType) {
    case ACTIVITY_TYPES.EVENING:
      durationSeconds = DEFAULT_DURATION.EVENING;
      break;
    case ACTIVITY_TYPES.BREAKING:
      durationSeconds = DEFAULT_DURATION.BREAKING;
      break;
    case ACTIVITY_TYPES.STANDALONE:
    case ACTIVITY_TYPES.CUSTOM_ROUTINES:
      durationSeconds = DEFAULT_DURATION.STANDALONE;
      break;
    case ACTIVITY_TYPES.LIBRARY:
      durationSeconds = DEFAULT_DURATION.LIBARARY;
      break;
    default:
      break;
  }

  return { ...base, duration_seconds: durationSeconds } as ActivityType;
};

export const resolveTimerDurationSeconds = (
  durationSeconds: number | null | undefined,
  activityType: string
): number => {
  if (isNumber(durationSeconds) && durationSeconds > 0) {
    return durationSeconds;
  }

  return getDefaultNewHabitBaseline(activityType).duration_seconds;
};

export const getTimerModeToggleResult = ({
  useTimer,
  durationSeconds,
  completionRequirements,
  cachedCompletion,
  cachedDuration,
  activityType
}: TimerModeToggleInput): TimerModeToggleResult => {
  if (useTimer) {
    const nextDuration = resolveTimerDurationSeconds(
      durationSeconds ?? cachedDuration,
      activityType
    );
    return {
      cachedCompletion: completionRequirements ?? cachedCompletion,
      cachedDuration: nextDuration,
      nextDuration,
      shouldUpdateDuration: durationSeconds !== nextDuration
    };
  }

  return {
    cachedCompletion,
    cachedDuration: durationSeconds ?? cachedDuration,
    nextCompletion: cachedCompletion,
    nextDuration: durationSeconds ?? cachedDuration,
    shouldUpdateDuration: false
  };
};

export const applyTimerModeToChoice = (
  choice: ChoiceType,
  result: TimerModeToggleResult
): ChoiceType => {
  const withDuration = { ...choice, duration_seconds: result.nextDuration };
  if (result.nextCompletion === undefined) {
    const { completion_requirements: _removed, ...rest } = withDuration;
    return rest as ChoiceType;
  }

  return {
    ...withDuration,
    completion_requirements: result.nextCompletion
  };
};

export const hasMoreOptionsFieldChanges = (
  activity: ActivityType,
  baseline: ActivityType
): boolean => {
  if (!areDaysOfWeekSetsEqual(activity.days_of_week, baseline.days_of_week)) {
    return true;
  }

  return !isEqual(
    pickMoreOptionsFields(activity),
    pickMoreOptionsFields(baseline)
  );
};

export const isBlankHabitDroppedOnSave = (activity: ActivityType): boolean =>
  !normalizeHabitName(activity.name ?? '');

/** Blank name + unchanged more-options fields (modal save gate). */
export const isIgnorableBlankHabit = (
  activity: ActivityType,
  activityType: string
): boolean => {
  if (!isBlankHabitDroppedOnSave(activity)) {
    return false;
  }

  const baseline = getDefaultNewHabitBaseline(activityType);
  return !hasMoreOptionsFieldChanges(activity, baseline);
};

export const hasBlankHabitsDroppedOnSave = (
  settings: SettingsType
): boolean => {
  const routineActivityLists = [
    settings.morning_activities,
    settings.break_activities,
    settings.evening_activities,
    settings.standalone_activities
  ];

  if (
    routineActivityLists.some((activities) =>
      activities?.some(isBlankHabitDroppedOnSave)
    )
  ) {
    return true;
  }

  return (
    settings.custom_routines?.some((routine) =>
      routine.standalone_activities?.some(isBlankHabitDroppedOnSave)
    ) ?? false
  );
};

export const getHabitDisplayName = (activity: ActivityType): string => {
  const normalizedName = normalizeHabitName(activity.name ?? '');
  return normalizedName || t('validation.untitled_habit');
};

export type HabitValidationNavigation = {
  activityId?: string;
  activityType: string;
  habitPosition: number;
  customRoutinePosition?: number;
  habitName: string;
};

export const buildHabitValidationNavigation = ({
  activity,
  activityType,
  habitPosition,
  customRoutinePosition
}: {
  activity: ActivityType;
  activityType: string;
  habitPosition: number;
  customRoutinePosition?: number;
}): HabitValidationNavigation => ({
  activityId: activity.id,
  activityType,
  habitPosition,
  customRoutinePosition,
  habitName: getHabitDisplayName(activity)
});
